import React, { useState, Fragment, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { privateAxios } from "../utils/axios";
import toast from "react-hot-toast";

function EditSupplier({
  supplier,
  editSupplierModalSetting,
  handlePageUpdate,
}) {
  const [formData, setFormData] = useState({
    name: supplier.name || "",
    storeName: supplier.storeName || "",
    phone: supplier.phone || "",
    phone2: supplier.phone2 || "",
    email: supplier.email || "",
    address: supplier.address || "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);
  const queryClient = useQueryClient();

  // Input refs for keyboard navigation
  const nameRef = useRef();
  const storeNameRef = useRef();
  const phoneRef = useRef();
  const phone2Ref = useRef();
  const emailRef = useRef();
  const addressRef = useRef();

  const editSupplierMutation = useMutation({
    mutationFn: async (updatedData) => {
      const response = await privateAxios.put(
        `/suppliers/${supplier._id}`,
        updatedData
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("Supplier updated successfully!");
      setError("");
      setTimeout(() => {
        queryClient.invalidateQueries(["suppliers"]);
        handlePageUpdate();
        editSupplierModalSetting();
      }, 1000);
    },
    onError: (error) => {
      setError(error.response?.data?.message || "Failed to update supplier");
      setSuccess("");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.storeName ||
      !formData.phone ||
      !formData.address
    ) {
      setError("Please fill in all required fields");
      return;
    }

    // Create update data object with required fields
    const updateData = {
      name: formData.name,
      storeName: formData.storeName,
      phone: formData.phone,
      address: formData.address,
    };

    // Only add optional fields if they have values
    if (formData.email?.trim()) {
      updateData.email = formData.email;
    }
    if (formData.phone2?.trim()) {
      updateData.phone2 = formData.phone2;
    }

    editSupplierMutation.mutate(updateData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      switch (field) {
        case "name":
          storeNameRef.current.focus();
          break;
        case "storeName":
          phoneRef.current.focus();
          break;
        case "phone":
          phone2Ref.current.focus();
          break;
        case "phone2":
          emailRef.current.focus();
          break;
        case "email":
          addressRef.current.focus();
          break;
        case "address":
          handleSubmit(e);
          break;
        default:
          break;
      }
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-gray-100">
                {/* Close button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={() => editSupplierModalSetting()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="bg-gradient-to-b from-white to-gray-50 px-6 pt-6 pb-4 sm:p-6">
                  <div className="w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100"
                    >
                      Edit Supplier
                    </Dialog.Title>

                    {error && (
                      <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                      </div>
                    )}

                    {success && (
                      <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
                        {success}
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label
                          htmlFor="name"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Supplier Name *
                        </label>
                        <input
                          ref={nameRef}
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "name")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="storeName"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Store Name *
                        </label>
                        <input
                          ref={storeNameRef}
                          type="text"
                          name="storeName"
                          id="storeName"
                          value={formData.storeName}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "storeName")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="phone"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Phone *
                        </label>
                        <input
                          ref={phoneRef}
                          type="tel"
                          name="phone"
                          id="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "phone")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="phone2"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Alternative Phone
                        </label>
                        <input
                          ref={phone2Ref}
                          type="tel"
                          name="phone2"
                          id="phone2"
                          value={formData.phone2}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "phone2")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Email
                        </label>
                        <input
                          ref={emailRef}
                          type="email"
                          name="email"
                          id="email"
                          value={formData.email}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "email")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="address"
                          className="block mb-2 text-sm font-medium text-gray-700"
                        >
                          Address *
                        </label>
                        <textarea
                          ref={addressRef}
                          name="address"
                          id="address"
                          value={formData.address}
                          onChange={handleChange}
                          onKeyDown={(e) => handleKeyDown(e, "address")}
                          className="bg-white border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-200 text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none"
                          required
                          rows="3"
                        />
                      </div>
                    </form>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl border-t border-gray-100">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto transition-all duration-150 ${
                      editSupplierMutation.isLoading
                        ? "opacity-60 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleSubmit}
                    disabled={editSupplierMutation.isLoading}
                  >
                    {editSupplierMutation.isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          ></path>
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      "Update Supplier"
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-150"
                    onClick={() => editSupplierModalSetting()}
                    ref={cancelButtonRef}
                    disabled={editSupplierMutation.isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default EditSupplier;
